import { StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { makeObservable, action, computed, observable, runInAction } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { pofWalletAggregate } from 'src_server/types/accountData';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { DateTime } from 'src_common/utils/time/time';
import { getTransactionsTotalDeposits } from 'src_server/types/wallet';
import { Amount } from 'src_common/common/amount/Amount';
import { Common } from 'src/AppState/Common';
import { ApiMarkersOfHarm } from 'src/components/MarkersOfHarm/ApiMarkersOfHarm';
import { GamprotectUserStore } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/Gamprotect/Gamprotect.store';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { apiGetSelfExclusionStatusRequest } from 'src/api_openapi/v2/compliance/apiGetSelfExclusionStatus';
import { ComplianceExclusionInfoFullType } from 'src/api_openapi/v2/compliance/schemas';
import { apiVerificationsRequest } from 'src/api_openapi/v2/idMission/apiVerifications';
import {
    IdentityVerificationStatusResponseType,
    IdentityEkycVerificationProgressType,
    IdentityEkycVerificationResultType,
} from 'src/api_openapi/v2/idMission/schemas';
import { GamstopUserStore } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/Gamstop/Gamstop.store';

export interface CustomerOptionType {
    id: CustomerOptionsIdType;
    label: string;
    value: string;
    loading?: boolean;
}

const BADGE_CLASS = {
    success: 'badge badge__success',
    fail: 'badge badge__red',
    warning: 'badge badge__orange',
    warningSoft: 'badge badge__yellow',
    white: 'badge',
    darkGreen: 'dark-green',
    mediumGreen: 'medium-green',
    lightGreen: 'light-green',
    lightRed: 'light-red',
    mediumRed: 'medium-red',
    darkRed: 'dark-red',
    default: 'default',
} as const;

const KYC_STATUSES = {
    passed: 'Passed',
    required: 'Required',
    failed: 'Failed',
    requested: 'Requested',
    pending: 'Pending',
    'not-checked': 'Not checked',
    'no-id-received': 'No ID Received',
};

const AV_STATUSES = {
    'not-checked': 'Not checked',
    passed: 'OK',
    failed: 'Failed',
    unknown: 'Unknown',
};

const GAMSTOP_STATUSES_OLD = {
    'not-checked': 'No',
    passed: 'No',
    'not-passed': 'Yes',
    unknown: 'No',
};

export const AMLRISK_STATUSES = {
    low: 'Low',
    'medium-low': 'Medium/Low',
    'medium-high': 'Medium/High',
    high: 'High',
};

export const MOH_RISK_LEVEL = {
    none: 'No risk',
    'very-low': 'Very low',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    'very-high': 'Very high',
};

export const DECEASED_LEVEL = {
    passed: 'NO',
    failed: 'YES',
    'not-checked': 'NOT CHECKED',
};

export const ID_MISSION_STATUS = {
    NOT_CHECKED: 'not-checked',
    REQUESTED: 'requested',
    PASSED: 'passed',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
} as const satisfies Record<string, IdentityEkycVerificationProgressType>;

export const ID_MISSION_STATUSES: Record<IdentityEkycVerificationProgressType, string> = {
    [ID_MISSION_STATUS.NOT_CHECKED]: 'Not checked',
    [ID_MISSION_STATUS.REQUESTED]: 'Requested',
    [ID_MISSION_STATUS.PASSED]: 'Passed',
    [ID_MISSION_STATUS.FAILED]: 'Failed',
    [ID_MISSION_STATUS.CANCELLED]: 'Cancelled',
};

export const ID_MISSION_RESULT = {
    SUCCESS: 'success',
    FAILURE: 'failure',
    NOT_CHECKED: 'not-checked',
    CANCELLED: 'cancelled',
} as const satisfies Record<string, IdentityEkycVerificationResultType>;

export const ID_MISSION_RESULT_STATUSES: Record<IdentityEkycVerificationResultType, string> = {
    [ID_MISSION_RESULT.SUCCESS]: 'Success',
    [ID_MISSION_RESULT.FAILURE]: 'Failure',
    [ID_MISSION_RESULT.NOT_CHECKED]: 'Not checked',
    [ID_MISSION_RESULT.CANCELLED]: 'Cancelled',
};

type CustomerOptionsIdType =
    | 'ageVerification'
    | 'amlRisk'
    | 'deceasedStatus'
    | 'depositLimits'
    | 'gamStopVerificationOld'
    | 'gamstop'
    | 'gamprotect'
    | 'identityResult'
    | 'identityScore'
    | 'idMissionFaceId'
    | 'idMissionIdCard'
    | 'idMissionPoa'
    | 'idMissionResult'
    | 'isHVC'
    | 'kycStatus'
    | 'mohRiskLevel'
    | 'pep'
    | 'playBreakExpiry'
    | 'pofStatus'
    | 'promoBanned'
    | 'sanctions'
    | 'selfExclusionExpiry'
    | 'srgStatus'
    | 'status';

export class CustomerSummaryTabSummaryOptionsState {
    private apiMarkersOfHarm: ApiMarkersOfHarm;
    public readonly getInteractionsResourceRisk: Resource<string>;
    public readonly gamprotectUserStore: GamprotectUserStore;
    public readonly gamstopUserStore: GamstopUserStore;
    public readonly auditIdMissionResource: Resource<IdentityVerificationStatusResponseType | null>;

    @observable public exclusionStatus: ComplianceExclusionInfoFullType | null | undefined = undefined;

    public constructor(
        private readonly customerId: number,
        private readonly models: ModelsState,
        private readonly pofWalletAggregateResource: Resource<pofWalletAggregate.ResponseType> | null,
        private readonly transactionTotalDepositsResource: Resource<getTransactionsTotalDeposits.ResponseType>,
        private readonly common: Common,
        private readonly configState: ConfigState
    ) {
        makeObservable(this);

        this.apiMarkersOfHarm = new ApiMarkersOfHarm(this.common);
        this.gamprotectUserStore = new GamprotectUserStore(common, customerId);
        this.gamstopUserStore = new GamstopUserStore(common, customerId);

        this.getInteractionsResourceRisk = new Resource(async (): Promise<string> => {
            const response = await this.apiMarkersOfHarm.getListMarkersOfHarm({
                account_id: this.customerId.toString(),
            });
            if (response.status === 200) {
                const responseArray = response.body.interactions.filter(
                    (interaction) => interaction.agent.toLowerCase() !== 'automation'
                );
                return responseArray[responseArray.length - 1]?.riskLevel ?? 'default';
            }

            return 'default';
        });

        if (this.configState.config.complianceNew) {
            void this.fetchSelfExclusionStatus();
        }

        this.auditIdMissionResource = new Resource(async () => {
            const { idMission } = this.configState.config;
            if (idMission === false) {
                return null;
            }

            const accountId = this.customerId;
            const { gatewayUrl } = this.common.appEnvParams;
            const response = await apiVerificationsRequest(
                gatewayUrl,
                { account_id: accountId, latest_only: true },
                { authorization: `Bearer ${this.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                return response.body[0] ?? null;
            } else if (response.status === 404) {
                return null;
            } else {
                throw new Error(`API returned status ${response.status}`);
            }
        });
    }

    @action public async fetchSelfExclusionStatus(): Promise<void> {
        const { gatewayUrl } = this.common.appEnvParams;
        const response = await apiGetSelfExclusionStatusRequest(
            gatewayUrl,
            {
                requestBody: [
                    {
                        accountId: this.customerId,
                        universe: this.configState.universe,
                    },
                ],
            },
            { authorization: `Bearer ${this.common.session.currentJwt}` }
        );

        if (response.status === 200) {
            runInAction(() => {
                const first = response.body[0];
                this.exclusionStatus = first?.status ?? null;
            });
        }
    }

    @computed public get auditData(): IdentityVerificationStatusResponseType | null {
        const result = this.auditIdMissionResource.get();

        if (result.type === 'ready') {
            return result.value;
        }
        return null;
    }

    @computed public get getInteractionsListRisk(): string {
        const interactionsList = this.getInteractionsResourceRisk.get();

        if (interactionsList.type === 'ready') {
            return interactionsList.value;
        }

        return 'default';
    }

    @computed public get getGamprotect(): boolean | null {
        const hasGamprotect = this.gamprotectUserStore.hasGamprotect;

        if (hasGamprotect === null) {
            return null;
        }

        return hasGamprotect;
    }

    @computed public get basicData(): StaffAccountBasicDataType | null {
        return this.models.getUserModel(this.customerId).basicData.data;
    }

    @computed public get pofWalletAggregateData(): pofWalletAggregate.SuccessResponseType | null {
        if (this.pofWalletAggregateResource !== null) {
            const response = this.pofWalletAggregateResource.getReady();

            if (response?.responseStatus === 'success') {
                return response.data;
            }
        }
        return null;
    }

    private transactionTotalDepositsData(): getTransactionsTotalDeposits.SuccessResponseType | null {
        const response = this.transactionTotalDepositsResource.get();
        if (this.pofWalletAggregateResource !== null) {
            if (response.type === 'ready' && response.value.responseStatus === 'success') {
                return response.value.data;
            }
        }
        return null;
    }

    @action public totalDeposits(): string | null {
        const currency = this.models.getUserModel(this.customerId).walletData.data?.currency ?? 'XTS';
        const transactionDeposits = this.transactionTotalDepositsData();
        return transactionDeposits === null ? null : new Amount(transactionDeposits.totalDeposits).format(currency); //amount in pounds!~not cents
    }

    @action public profit(): string | null {
        const currency = this.models.getUserModel(this.customerId).walletData.data?.currency ?? 'XTS';
        const transactionDeposits = this.transactionTotalDepositsData();

        return transactionDeposits === null ? null : new Amount(transactionDeposits.profit).format(currency); //probably amount in pounds!~not cents
    }

    @computed public get pofTotalNetLoss(): string {
        const checkValue =
            this.pofWalletAggregateData?.totalNetLoss === undefined
                ? undefined
                : -1 * parseFloat(this.pofWalletAggregateData.totalNetLoss);
        return this.parseNumberView(checkValue?.toString());
    }

    @action public parseNumberView = (entryValue?: string): string => {
        if (entryValue === undefined) {
            return '0.00';
        }
        return `${parseFloat(entryValue).toFixed(2)}`;
    };
    @action public objectContains<T extends Record<string, string>>(
        obj: T,
        term: string | boolean | undefined,
        defaultResponse: string
    ): string {
        if (typeof term === 'string' && term in obj) {
            return obj[term] ?? defaultResponse;
        }

        return defaultResponse;
    }

    @action public hasExpired = (date: string): boolean => {
        const formatDate2 = DateTime.from(date) ?? DateTime.current();
        const formatDateNow2 = DateTime.current();

        return !formatDate2.isAfter(formatDateNow2);
    };

    @computed public get customerOptionsFirstColumn(): CustomerOptionType[] {
        if (this.basicData === null) {
            return [];
        }

        return [
            {
                id: 'playBreakExpiry',
                label: 'Playbreak',
                value: ((): string => {
                    const value = this.basicData.playBreakExpiry;
                    if (value === null || value === undefined) {
                        return 'Not Set';
                    }
                    return this.hasExpired(value) ? 'Expired' : 'Set';
                })(),
            },
            {
                id: 'selfExclusionExpiry',
                label: 'Self excl.',
                loading: this.configState.config.complianceNew && this.exclusionStatus === undefined,
                value: ((): string => {
                    if (this.configState.config.complianceNew) {
                        const status = this.exclusionStatus;
                        if (status === undefined || status === null) {
                            return 'Not Set';
                        }
                        const expiry = DateTime.from(status.reactivationAllowedAfter);
                        if (expiry !== null && this.hasExpired(status.reactivationAllowedAfter)) {
                            return 'Expired';
                        }
                        return 'Set';
                    }
                    // legacy - to  remove when 'complianceNew' will be deployed on all operators
                    const value = this.basicData.selfExclusionExpiry;
                    if (value === null || value === undefined) {
                        return 'Not Set';
                    }
                    return this.hasExpired(value) ? 'Expired' : 'Set';
                })(),
            },
            {
                id: 'ageVerification',
                label: 'AV',
                value: this.objectContains(AV_STATUSES, this.basicData.ageVerification, 'not-checked'),
            },
        ];
    }

    @computed public get customerOptionsSecondColumn(): Array<CustomerOptionType> {
        if (this.basicData === null) {
            return [];
        }
        const output: Array<CustomerOptionType> = [];

        if (this.configState.config.gamstopNew === true) {
            output.push({
                id: 'gamstop',
                label: 'Gamstop',
                value: this.gamstopUserStore.status === 'P' ? 'PP' : this.gamstopUserStore.status,
            });
        } else {
            output.push({
                id: 'gamStopVerificationOld',
                label: 'GamStop excl',
                value: this.objectContains(GAMSTOP_STATUSES_OLD, this.basicData.gamStopVerification, 'not-checked'),
            });
        }

        if (this.configState.config.gamprotect !== false) {
            output.push({
                id: 'gamprotect',
                label: 'Gamprotect excl',
                value: this.getGamprotect === true ? 'YES' : 'NO',
            });
        }

        output.push(
            {
                id: 'kycStatus',
                label: 'KYC',
                value: this.objectContains(KYC_STATUSES, this.basicData.kycStatus, 'not-checked'),
            },
            {
                id: 'promoBanned',
                label: 'Promo Banned',
                value: this.basicData.promoBanned === true ? 'Yes' : 'No',
            }
        );

        return output;
    }

    @action public getClassName(properName: CustomerOptionsIdType): string {
        if (this.basicData === null) {
            return BADGE_CLASS.white;
        }

        switch (properName) {
            case 'selfExclusionExpiry':
                if (this.configState.config.complianceNew) {
                    const status = this.exclusionStatus;
                    if (status === undefined) {
                        return BADGE_CLASS.white;
                    }
                    if (status === null) {
                        return BADGE_CLASS.success;
                    }
                    return this.hasExpired(status.reactivationAllowedAfter) ? BADGE_CLASS.warning : BADGE_CLASS.fail;
                }
                // legacy
                if (this.basicData.selfExclusionExpiry === null || this.basicData.selfExclusionExpiry === undefined) {
                    return BADGE_CLASS.success;
                }
                return this.hasExpired(this.basicData.selfExclusionExpiry) ? BADGE_CLASS.warning : BADGE_CLASS.fail;
            case 'playBreakExpiry':
                if (this.basicData.playBreakExpiry === null || this.basicData.playBreakExpiry === undefined) {
                    return BADGE_CLASS.success;
                }
                return this.hasExpired(this.basicData.playBreakExpiry) ? BADGE_CLASS.warning : BADGE_CLASS.fail;
            case 'kycStatus':
                switch (this.basicData.kycStatus) {
                    case 'passed':
                        return BADGE_CLASS.success;
                    case 'failed':
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.warning;
                }

            case 'gamprotect':
                switch (this.getGamprotect) {
                    case true:
                        return BADGE_CLASS.fail;

                    default:
                        return BADGE_CLASS.success;
                }
            case 'ageVerification':
                switch (this.basicData.ageVerification) {
                    case 'passed':
                        return BADGE_CLASS.success;
                    case 'failed':
                        return BADGE_CLASS.fail;
                    case 'unknown':
                        return BADGE_CLASS.warning;
                    default:
                        return BADGE_CLASS.white;
                }
            case 'gamStopVerificationOld':
                switch (this.basicData.gamStopVerification) {
                    case 'not-passed':
                        return BADGE_CLASS.fail;
                    case 'not-checked':
                        return BADGE_CLASS.warning;
                    default:
                        return BADGE_CLASS.success;
                }
            case 'gamstop':
                switch (this.gamstopUserStore.status) {
                    case 'Y':
                        return BADGE_CLASS.fail;
                    case 'P':
                        return BADGE_CLASS.warning;
                    case 'N':
                        return BADGE_CLASS.success;
                    case 'unknown':
                        return BADGE_CLASS.warning;
                    case 'error':
                        return BADGE_CLASS.warning;
                    case 'loading':
                        return BADGE_CLASS.white;
                    default:
                        return BADGE_CLASS.success;
                }
            case 'status':
                switch (this.basicData.status) {
                    case 'active':
                        return BADGE_CLASS.success;
                    case 'blocked':
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.warning;
                }
            case 'promoBanned':
                switch (this.basicData.promoBanned) {
                    case true:
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.success;
                }
            case 'srgStatus':
                switch (this.basicData.srgStatus) {
                    case 'not-started':
                        return BADGE_CLASS.white;
                    case 'requested':
                        return BADGE_CLASS.warning;
                    case 'passed':
                        return BADGE_CLASS.success;
                    case 'failed':
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.white;
                }
            case 'isHVC':
                switch (this.basicData.isHVC) {
                    case true:
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.success;
                }
            case 'pofStatus':
                switch (this.basicData.pofStatus) {
                    case 'not-checked':
                        return BADGE_CLASS.white;
                    case 'requested':
                        return BADGE_CLASS.warning;
                    case 'passed':
                        return BADGE_CLASS.success;
                    case 'failed':
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.white;
                }
            case 'pep':
                switch (this.basicData.kycWatchlist?.pep) {
                    case true:
                        return BADGE_CLASS.fail;
                    case false:
                        return BADGE_CLASS.success;
                    default:
                        return BADGE_CLASS.warning;
                }
            case 'sanctions':
                switch (this.basicData.kycWatchlist?.sanctions) {
                    case true:
                        return BADGE_CLASS.fail;
                    case false:
                        return BADGE_CLASS.success;
                    default:
                        return BADGE_CLASS.warning;
                }
            case 'amlRisk':
                switch (this.basicData.amlRisk) {
                    case 'low':
                        return BADGE_CLASS.success;
                    case 'medium-low':
                        return BADGE_CLASS.warningSoft;
                    case 'medium-high':
                        return BADGE_CLASS.warning;
                    case 'high':
                        return BADGE_CLASS.fail;
                    default:
                        return BADGE_CLASS.white;
                }
            case 'mohRiskLevel':
                switch (this.getInteractionsListRisk) {
                    case 'none':
                        return BADGE_CLASS.darkGreen;
                    case 'very-low':
                        return BADGE_CLASS.mediumGreen;
                    case 'low':
                        return BADGE_CLASS.lightGreen;
                    case 'medium':
                        return BADGE_CLASS.lightRed;
                    case 'high':
                        return BADGE_CLASS.mediumRed;
                    case 'very-high':
                        return BADGE_CLASS.darkRed;
                    default:
                        return BADGE_CLASS.default;
                }
            case 'identityScore': {
                const { identityScore } = this.basicData;

                if (identityScore === undefined || identityScore === null) {
                    return BADGE_CLASS.white;
                }

                if (identityScore < 40) {
                    return BADGE_CLASS.fail;
                }

                return BADGE_CLASS.success;
            }

            case 'identityResult': {
                const { identityResult } = this.basicData;

                if (identityResult === undefined || identityResult === null) {
                    return BADGE_CLASS.white;
                }

                if (identityResult === 'Fail') {
                    return BADGE_CLASS.fail;
                }

                return BADGE_CLASS.success;
            }
            case 'deceasedStatus': {
                if (this.basicData.deceasedStatus === 'passed') {
                    return BADGE_CLASS.success;
                }

                if (this.basicData.deceasedStatus === 'failed') {
                    return BADGE_CLASS.fail;
                }

                return BADGE_CLASS.warning;
            }
            case 'idMissionPoa':
                return this.getIdMissionStatusClassName(this.auditData?.verification.verificationPoaStatus);
            case 'idMissionIdCard':
                return this.getIdMissionStatusClassName(this.auditData?.verification.verificationIdStatus);
            case 'idMissionFaceId':
                return this.getIdMissionStatusClassName(this.auditData?.verification.verificationFaceStatus);
            case 'idMissionResult':
                return this.getIdMissionResultClassName(this.auditData?.verification.verificationResult);
            default:
                return BADGE_CLASS.success;
        }
    }

    @computed public get pendingSuspensionTimeActive(): boolean {
        const complianceRestriction = this.basicData?.complianceRestriction ?? null;

        if (complianceRestriction !== null) {
            return true;
        }
        return false;
    }

    @computed public get pendingDepositRestrictionActive(): boolean {
        const depositRestiction = this.basicData?.depositRestriction ?? null;
        const expirityDate = depositRestiction?.expiryDate ?? null;

        if (depositRestiction === null) {
            return false;
        }

        if (depositRestiction.permanent === true) {
            return true;
        }

        if (expirityDate === null) {
            return false;
        }

        return DateTime.current().isBefore(expirityDate);
    }

    @action public modifyOptionalBooleanStatus = (status?: boolean): string => {
        if (status === true) {
            return 'Yes';
        } else if (status === false) {
            return 'No';
        }
        return 'Unknown';
    };

    @action private getIdMissionStatusClassName(status: IdentityEkycVerificationProgressType | undefined): string {
        switch (status) {
            case ID_MISSION_STATUS.NOT_CHECKED:
                return BADGE_CLASS.white;
            case ID_MISSION_STATUS.REQUESTED:
                return BADGE_CLASS.warning;
            case ID_MISSION_STATUS.PASSED:
                return BADGE_CLASS.success;
            case ID_MISSION_STATUS.FAILED:
                return BADGE_CLASS.fail;
            default:
                return BADGE_CLASS.white;
        }
    }

    @action private getIdMissionResultClassName(result: IdentityEkycVerificationResultType | undefined): string {
        switch (result) {
            case ID_MISSION_RESULT.NOT_CHECKED:
                return BADGE_CLASS.white;
            case ID_MISSION_RESULT.SUCCESS:
                return BADGE_CLASS.success;
            case ID_MISSION_RESULT.FAILURE:
                return BADGE_CLASS.fail;
            default:
                return BADGE_CLASS.white;
        }
    }
}
